import { Box } from '@mui/material'
import React, { useMemo } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'

import Card from '@/components/Card'
import ColorSelection from '@/components/ColorSelection'
import LabelWithTooltip from '@/components/LabelWithTooltip'
import RangeSlider from '@/components/RangeSlider'
import Select from '@/components/Select'
import { useAppSelector } from '@/hooks/redux'
import type { Metadata } from '@/pages/stainingStudio/crown/StaingingStudio'
import { useGetMetadataQuery } from '@/store/apiService'
import {
  setOpaquerCeramicBrand,
  setOpaquerRangeSlider,
  setSelectedOpaquerKey,
} from '@/store/pfmCrown'
import { REGION, SLIDER_CLASS, sliderRange } from '@/utils/constants'
import { getOptions, sortAlphabetically } from '@/utils/helper'

function OpaquerPanel({
  containerRef,
}: {
  containerRef?: React.RefObject<HTMLDivElement>
}) {
  const { t } = useTranslation('translation', { keyPrefix: 'gingiva' })
  const { t: translate } = useTranslation()

  const dispatch = useDispatch()
  const { data: metadata } = useGetMetadataQuery(undefined, {
    refetchOnMountOrArgChange: true,
  }) as {
    data: Metadata | undefined
    error: any
    isLoading: boolean
  }

  const ceramicBrands = useMemo(
    () =>
      metadata
        ? sortAlphabetically(
            getOptions(metadata, 'available_ceramic_brands'),
            'text',
          )
        : [],
    [metadata],
  )

  const canvasImage = useAppSelector((state) => state.app.canvasImage)

  const sliderValues = useAppSelector(
    (state) => state.pfmCrown.opaquerSliderValue,
  )

  const opaquerCeramicBrand = useAppSelector(
    (state) => state.pfmCrown.opaquerCeramicBrand,
  )

  const selectedOpaquerKey = useAppSelector(
    (state) => state.pfmCrown.selectedOpaquerKey,
  )

  const opaquer = useAppSelector((state) => state.pfmCrown.opaquer)

  const { control, setValue, clearErrors } = useForm({
    defaultValues: {
      opaquerCeramicBrand: opaquerCeramicBrand,
    },
    mode: 'onChange',
  })

  return (
    <Box
      display="flex"
      flexDirection="column"
      flexGrow={1}
      gap={1}
      overflow="auto"
    >
      <Card>
        <Select
          options={ceramicBrands}
          title={t('ceramicBrand')}
          tooltipText={t('ceramicBrandTooltip')}
          control={control}
          name="opaquerCeramicBrand"
          onFormValueChange={(name, value) => {
            dispatch(setOpaquerCeramicBrand(value))
          }}
          optionDisabledChecker={(item) =>
            !metadata?.compatibility_brand_indication?.[item.value]?.includes(
              'PFM Crown',
            )
          }
        />
      </Card>
      {Object.keys(REGION)?.map((r) => (
        <>
          <Card key={r}>
            <LabelWithTooltip
              tooltipText={t('availableSpaceTooltip')}
              label={translate(REGION[r as keyof typeof REGION])}
            />
            <RangeSlider
              label={t('availableSpace')}
              name={`${r}`}
              control={control}
              sliderRange={sliderRange}
              step={0.1}
              className={SLIDER_CLASS[r as keyof typeof SLIDER_CLASS]}
              onFormValueChange={(name, value) => {
                dispatch(setOpaquerRangeSlider({ name, value }))
              }}
              selectedValue={sliderValues[r] || 0.5}
            />

            <ColorSelection
              control={control}
              name={r}
              title={t('targetColor')}
              error={false}
              clearErrors={clearErrors}
              setValue={setValue}
              data={opaquer?.[canvasImage?.id]?.[r]?.targetcolor}
              selectedPickerKey={selectedOpaquerKey}
              onclick={() => {
                dispatch(setSelectedOpaquerKey(r))
              }}
              containerRef={containerRef}
              snackbarClose={!selectedOpaquerKey}
            />
          </Card>
        </>
      ))}
    </Box>
  )
}

export default OpaquerPanel
